/**
 * Task 15 — remote repository against Supabase (docs/08 §6, §8c).
 * Read/write + Zod validation + stripServerFields. Not sync / outbox flush.
 */

import type { SyncedTableName } from '../schemas/local-only';
import { APPEND_ONLY_TABLES, ENTITY_SCHEMAS, IMMUTABLE_TABLES } from './entityRegistry';
import {
  classifyTransportError,
  RemoteRepositoryError,
  type RemoteOperation,
} from './remoteErrors';
import type { CompoundCursor, OptimisticWriteOptions, RemoteTransport } from './remoteTransport';
import { stripServerFields } from './stripServerFields';

export type { OptimisticWriteOptions, CompoundCursor };

export type RemoteRepository = {
  insert(table: SyncedTableName, payload: unknown): Promise<Record<string, unknown>>;
  update(
    table: SyncedTableName,
    id: string,
    payload: unknown,
    options?: OptimisticWriteOptions,
  ): Promise<Record<string, unknown>>;
  softDelete(
    table: SyncedTableName,
    id: string,
    deletedAt: string,
    options?: OptimisticWriteOptions,
  ): Promise<Record<string, unknown>>;
  selectById(table: SyncedTableName, id: string): Promise<Record<string, unknown> | null>;
  list(table: SyncedTableName): Promise<readonly Record<string, unknown>[]>;
  selectSince(
    table: SyncedTableName,
    cursor: CompoundCursor,
    limit: number,
  ): Promise<readonly Record<string, unknown>[]>;
};

function throwMapped(
  table: SyncedTableName,
  operation: RemoteOperation,
  error: { message: string; code?: string; status?: number },
): never {
  throw new RemoteRepositoryError({
    code: classifyTransportError(error),
    table,
    operation,
    ...(error.code !== undefined && error.code.length > 0 ? { detail: error.code } : {}),
  });
}

function parseRemoteRow(
  table: SyncedTableName,
  row: Record<string, unknown>,
): Record<string, unknown> {
  const parsed = ENTITY_SCHEMAS[table].remoteRow.safeParse(row);
  if (!parsed.success) {
    throw new RemoteRepositoryError({
      code: 'validation',
      table,
      operation: 'select',
      detail: `zod:${String(parsed.error.issues.length)}`,
    });
  }
  return parsed.data as Record<string, unknown>;
}

export function createRemoteRepository(transport: RemoteTransport): RemoteRepository {
  return {
    async insert(table, payload) {
      const schemas = ENTITY_SCHEMAS[table];
      const validated = schemas.remoteInsertPayload.safeParse(payload);
      if (!validated.success) {
        throw new RemoteRepositoryError({
          code: 'validation',
          table,
          operation: 'insert',
          detail: `zod:${String(validated.error.issues.length)}`,
        });
      }
      const wire = stripServerFields(validated.data as Record<string, unknown>);
      const { data, error } = await transport.insert(table, wire);
      if (error) throwMapped(table, 'insert', error);
      if (data === null) {
        throw new RemoteRepositoryError({
          code: 'unknown',
          table,
          operation: 'insert',
          detail: 'empty_return',
        });
      }
      return parseRemoteRow(table, data);
    },

    async update(table, id, payload, options) {
      if (APPEND_ONLY_TABLES.has(table)) {
        throw new RemoteRepositoryError({
          code: 'append_only_violation',
          table,
          operation: 'update',
        });
      }
      if (IMMUTABLE_TABLES.has(table)) {
        throw new RemoteRepositoryError({
          code: 'immutable_violation',
          table,
          operation: 'update',
        });
      }
      if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
        throw new RemoteRepositoryError({
          code: 'validation',
          table,
          operation: 'update',
          detail: 'payload_not_object',
        });
      }
      const wire = stripServerFields(payload as Record<string, unknown>);
      // id is the filter; do not allow overwriting primary key via body.
      delete wire['id'];
      const { data, error } = await transport.update(table, id, wire, options);
      if (error) throwMapped(table, 'update', error);
      if (data === null) {
        throw new RemoteRepositoryError({
          code: 'not_found',
          table,
          operation: 'update',
        });
      }
      return parseRemoteRow(table, data);
    },

    async softDelete(table, id, deletedAt, options) {
      if (APPEND_ONLY_TABLES.has(table)) {
        throw new RemoteRepositoryError({
          code: 'append_only_violation',
          table,
          operation: 'soft_delete',
        });
      }
      if (IMMUTABLE_TABLES.has(table)) {
        throw new RemoteRepositoryError({
          code: 'immutable_violation',
          table,
          operation: 'soft_delete',
        });
      }
      const { data, error } = await transport.softDelete(table, id, deletedAt, options);
      if (error) throwMapped(table, 'soft_delete', error);
      if (data === null) {
        throw new RemoteRepositoryError({
          code: 'not_found',
          table,
          operation: 'soft_delete',
        });
      }
      return parseRemoteRow(table, data);
    },

    async selectById(table, id) {
      const { data, error } = await transport.selectById(table, id);
      if (error) throwMapped(table, 'select', error);
      if (data === null) return null;
      return parseRemoteRow(table, data);
    },

    async list(table) {
      const { data, error } = await transport.list(table);
      if (error) throwMapped(table, 'list', error);
      return data.map((row) => parseRemoteRow(table, row));
    },

    async selectSince(table, cursor, limit) {
      const { data, error } = await transport.selectSince(table, cursor, limit);
      if (error) throwMapped(table, 'select_since', error);
      return data.map((row) => parseRemoteRow(table, row));
    },
  };
}
